import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, BookOpen, PenTool, Mic, ChevronRight, RotateCcw } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProficiencyTrainingModule = () => {
  const [trainingData, setTrainingData] = useState(null);
  const [activeModality, setActiveModality] = useState('writing');
  const [activeSample, setActiveSample] = useState(0);
  const [selectedScore, setSelectedScore] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/wida/rubric-training`)
      .then(r => { setTrainingData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const resetPractice = () => {
    setActiveSample(0);
    setSelectedScore(null);
    setShowAnswer(false);
    setScores({});
  };

  if (loading) {
    return <div className="flex justify-center p-12"><div className="loading-spinner" /></div>;
  }

  if (!trainingData) {
    return <div className="text-center text-gray-500 p-12">Unable to load training data.</div>;
  }

  const modData = trainingData[activeModality];
  if (!modData) return null;

  const sample = modData.practice_samples[activeSample];
  const totalSamples = modData.practice_samples.length;
  const totalCorrect = Object.values(scores).filter(s => s.correct).length;
  const totalAttempted = Object.keys(scores).length;

  return (
    <div className="space-y-6" data-testid="wida-training-module">
      {/* Header */}
      <div className="border-2 border-[#002FA7] p-6 bg-[#002FA7]/5">
        <div className="flex items-center gap-3 mb-3">
          <BookOpen className="text-[#002FA7]" size={24} />
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            WIDA Rubric Training Module
          </h2>
        </div>
        <p className="text-sm text-gray-600 max-w-3xl">
          Practice scoring student responses using official WIDA rubrics. This training helps you develop 
          consistent, standards-aligned scoring for Speaking and Writing responses. Review the rubric, 
          read the student response, then select a score (0-6).
        </p>
      </div>

      {/* Modality Selector */}
      <div className="flex gap-3" data-testid="training-modality-selector">
        <Button
          variant={activeModality === 'writing' ? 'default' : 'outline'}
          onClick={() => { setActiveModality('writing'); resetPractice(); }}
          className={`btn-sharp ${activeModality === 'writing' ? 'bg-[#002FA7] text-white' : ''}`}
          data-testid="training-writing-btn"
        >
          <PenTool size={16} className="mr-2" />
          Writing Rubric
        </Button>
        <Button
          variant={activeModality === 'speaking' ? 'default' : 'outline'}
          onClick={() => { setActiveModality('speaking'); resetPractice(); }}
          className={`btn-sharp ${activeModality === 'speaking' ? 'bg-[#002FA7] text-white' : ''}`}
          data-testid="training-speaking-btn"
        >
          <Mic size={16} className="mr-2" />
          Speaking Rubric
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: Rubric Reference */}
        <div className="border-2 border-gray-200" data-testid="rubric-reference">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="font-bold text-sm">{modData.rubric_name}</h3>
            <p className="text-xs text-gray-500 mt-1">
              Scoring Dimensions: {modData.dimensions.join(' + ')}
            </p>
          </div>
          <div className="divide-y divide-gray-100">
            {Object.entries(modData.levels).map(([score, desc]) => (
              <div key={score} className="px-4 py-3 flex gap-3" data-testid={`rubric-level-${score}`}>
                <div className={`w-8 h-8 flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                  parseInt(score) >= 5 ? 'bg-green-100 text-green-700' :
                  parseInt(score) >= 3 ? 'bg-yellow-100 text-yellow-700' :
                  parseInt(score) >= 1 ? 'bg-orange-100 text-orange-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {score}
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Practice Scoring */}
        <div className="space-y-4">
          {/* Progress */}
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-gray-600">
              Sample {activeSample + 1} of {totalSamples}
            </span>
            {totalAttempted > 0 && (
              <span className="text-xs px-2 py-1 bg-gray-100 font-medium">
                {totalCorrect}/{totalAttempted} correct
              </span>
            )}
          </div>

          {/* Student Response Card */}
          <div className="border-2 border-gray-200" data-testid="practice-sample-card">
            <div className="p-4 border-b border-gray-200">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">PROMPT (Grade {sample.grade})</div>
              <p className="text-sm font-medium">{sample.prompt}</p>
            </div>
            <div className="p-4 bg-gray-50">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">STUDENT RESPONSE</div>
              <div className="bg-white border border-gray-200 p-4 text-sm whitespace-pre-wrap leading-relaxed" data-testid="student-response-text">
                {sample.response}
              </div>
            </div>

            {/* Score Selection */}
            <div className="p-4 border-t border-gray-200">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">YOUR SCORE</div>
              <div className="flex gap-2 flex-wrap" data-testid="training-score-buttons">
                {[0, 1, 2, 3, 4, 5, 6].map(score => (
                  <Button
                    key={score}
                    variant="outline"
                    size="sm"
                    disabled={showAnswer}
                    onClick={() => {
                      setSelectedScore(score);
                      setShowAnswer(true);
                      setScores(prev => ({
                        ...prev,
                        [sample.id]: { selected: score, correct: score === sample.correct_score }
                      }));
                    }}
                    className={`w-11 h-11 p-0 btn-sharp font-bold text-base ${
                      showAnswer && score === sample.correct_score ? 'bg-green-500 text-white border-green-500' :
                      showAnswer && score === selectedScore && score !== sample.correct_score ? 'bg-red-100 text-red-700 border-red-400' :
                      selectedScore === score ? 'bg-[#002FA7] text-white border-[#002FA7]' : ''
                    }`}
                    data-testid={`training-score-${score}`}
                  >
                    {score}
                  </Button>
                ))}
              </div>

              {/* Answer Feedback */}
              {showAnswer && (
                <div className={`mt-4 p-4 border-2 ${selectedScore === sample.correct_score ? 'border-green-400 bg-green-50' : 'border-red-300 bg-red-50'}`} data-testid="training-feedback">
                  <div className="flex items-center gap-2 mb-2">
                    {selectedScore === sample.correct_score ? (
                      <>
                        <CheckCircle size={18} className="text-green-600" />
                        <span className="font-bold text-green-700 text-sm">Correct! Score: {sample.correct_score}</span>
                      </>
                    ) : (
                      <>
                        <XCircle size={18} className="text-red-600" />
                        <span className="font-bold text-red-700 text-sm">
                          Not quite. You scored {selectedScore}, correct answer is {sample.correct_score}.
                        </span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-gray-700 leading-relaxed">{sample.explanation}</p>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={resetPractice}
              className="btn-sharp"
              data-testid="training-restart"
            >
              <RotateCcw size={14} className="mr-1" /> Restart
            </Button>
            {showAnswer && activeSample < totalSamples - 1 && (
              <Button
                size="sm"
                onClick={() => { setActiveSample(prev => prev + 1); setSelectedScore(null); setShowAnswer(false); }}
                className="btn-sharp bg-[#002FA7] text-white"
                data-testid="training-next"
              >
                Next Sample <ChevronRight size={14} className="ml-1" />
              </Button>
            )}
            {showAnswer && activeSample === totalSamples - 1 && (
              <div className="text-sm font-bold text-[#002FA7]" data-testid="training-complete">
                Training Complete! {totalCorrect}/{totalSamples} correct
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProficiencyTrainingModule;
